<script lang="ts">
  import { onMount } from 'svelte';
  import { auth } from '$lib/stores/auth';

  type Factor = { id: string; status: string };

  let loading = true;
  let verifiedFactor: Factor | null = null;

  // Enrollment-in-progress state
  let enrolling: { factorId: string; qrCode: string; secret: string } | null = null;
  let code = '';

  async function refresh() {
    loading = true;
    const factors = (await auth.listFactors()) as Factor[];
    verifiedFactor = factors.find((f) => f.status === 'verified') ?? null;
    loading = false;
  }

  onMount(refresh);

  async function startEnroll() {
    // Clean up any abandoned, unverified factors before enrolling fresh.
    const factors = (await auth.listFactors()) as Factor[];
    for (const f of factors.filter((f) => f.status !== 'verified')) {
      await auth.unenrollTotp(f.id);
    }
    const result = await auth.enrollTotp();
    if (result) {
      enrolling = { factorId: result.factorId, qrCode: result.qrCode, secret: result.secret };
    }
  }

  async function confirmEnroll() {
    if (!enrolling) return;
    const ok = await auth.verifyTotp(enrolling.factorId, code);
    if (ok) {
      enrolling = null;
      code = '';
      await refresh();
    }
  }

  function cancelEnroll() {
    if (enrolling) void auth.unenrollTotp(enrolling.factorId);
    enrolling = null;
    code = '';
  }

  async function disable() {
    if (!verifiedFactor) return;
    const ok = await auth.unenrollTotp(verifiedFactor.id);
    if (ok) await refresh();
  }
</script>

<section class="security">
  <h2>Account security</h2>

  {#if $auth.error}
    <p class="error">{$auth.error}</p>
  {/if}

  {#if loading}
    <p class="info">Loading…</p>
  {:else if enrolling}
    <p class="info">Scan this QR code with your authenticator app, then enter the 6-digit code.</p>
    <div class="qr">
      {#if enrolling.qrCode.startsWith('data:')}
        <img src={enrolling.qrCode} alt="TOTP QR code" />
      {:else}
        {@html enrolling.qrCode}
      {/if}
    </div>
    <p class="secret">Or enter this secret manually: <code>{enrolling.secret}</code></p>
    <div class="fields">
      <input
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        maxlength="6"
        bind:value={code}
        placeholder="123456"
      />
      <button on:click={confirmEnroll} disabled={$auth.loading || code.length < 6}>Verify &amp; enable</button>
      <button class="secondary" on:click={cancelEnroll}>Cancel</button>
    </div>
  {:else if verifiedFactor}
    <p class="info">✅ Two-factor authentication is <strong>enabled</strong>.</p>
    <button on:click={disable}>Disable 2FA</button>
  {:else}
    <p class="info">Add an authenticator app for an extra layer of security.</p>
    <button on:click={startEnroll}>Enable 2FA</button>
  {/if}
</section>

<style>
  .security {
    max-width: 400px;
    width: 100%;
    margin: 2rem auto;
    padding: 1.5rem;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    text-align: center;
  }

  .qr {
    display: flex;
    justify-content: center;
    margin: 1rem 0;
  }

  .qr :global(svg),
  .qr img {
    width: 200px;
    height: 200px;
  }

  .secret {
    font-size: 0.85rem;
    color: #666;
    word-break: break-all;
  }

  .fields {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1rem;
  }

  input {
    padding: 0.75rem 1rem;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 1.25rem;
    text-align: center;
    letter-spacing: 0.3em;
  }

  button {
    padding: 0.6rem 1rem;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    font-size: 1rem;
  }

  button:hover {
    background: #f5f5f5;
  }

  button.secondary {
    color: #666;
  }

  .error {
    color: #d32f2f;
  }

  .info {
    color: #666;
  }
</style>
